import * as PIXI from 'pixi.js';
import { Config } from '../../video-data/config';

import { assertNumber } from '../../validators';
import { AlphaAttribute, ColorAttribute } from '../attributes';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A CircleAsset Object will load the specified solid color.
 */
export class CircleAsset extends BaseAsset {
    override _classType = Config.CIRCLE_ASSET_TYPE;

    @ColorAttribute()
    color = 0xEEEB77;
    #lineThickness = 0;
    @ColorAttribute()
    lineColor = 0xEEEB77;
    @AlphaAttribute()
    fillAlpha = 1;

    #radius = 10;
    #radiusOffset = 0;

    #graphics: PIXI.Graphics = undefined;

    constructor(container: any, settings: BaseAssetSettings) {
        super(container, settings);

        // compose object
        this.#graphics = new PIXI.Graphics();
        this._addInternalChild(this.#graphics);
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get shouldLoad() {
        return true;
    }

    get radius(): number {
        return this.#radius;
    }

    set radius(value) {
        if (value < 0) {
            value = 0;
        }
        if (this.radius !== value) {
            this.#radius = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Get the line thickness
     */
    get lineThickness(): number {
        return this.#lineThickness;
    }

    set lineThickness(value: number) {
        assertNumber(value);
        if (this.lineThickness !== value) {
            this.#lineThickness = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Get the radius offset
     */
    get radiusOffset(): number {
        return this.#radiusOffset;
    }

    set radiusOffset(value: number) {
        assertNumber(value);
        if (this.radiusOffset !== value) {
            this.#radiusOffset = value;
            this.requiresRedraw = true;
        }
    }

    override _build() {
        this.clearAnimation();
        this.applyTransformation();

        this.applyAnimation();
        this.applyIntroAnimations();
        this.applyOutroAnimations();
    }

    override _draw() {
        const totalRadius = this.radius + this.radiusOffset;

        this.#graphics.clear();
        this.#graphics.lineStyle(this.lineThickness, this.lineColor, 1);
        this.#graphics.beginFill(this.color, this.fillAlpha);
        this.#graphics.drawCircle(0, 0, totalRadius);
        this.#graphics.endFill();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'lineThickness',
            'lineColor',
            'color',
            'fillAlpha',
            'radius',
            'radiusOffset',
        ]);
    };
}
